// The post-apply open-tab pass (NAV1 amendment 2026-08-23).
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 34 (amended) + § NAV1.
//
// Runs after `executeRename` reports success — for a forward rename and for an
// undo alike. Three steps, each already owned by its own module:
//
//   1. COLLECT  the open query tabs on the renamed object's OWN connection,
//      each tagged with the database it is bound to. A tab pointed at another
//      database usually references a same-named object that was NOT renamed —
//      but it may also spell the renamed one in full (`B.dbo.users` typed from
//      an `A` tab, which is exactly how F2 renames across databases), so those
//      tabs are kept and scoped rather than dropped (see {@link tabScopeOf}).
//   2. PREFILTER with `scanTabsForRenamedIdentifier` — a cheap name-only token
//      scan that decides which buffers are worth parsing at all.
//   3. REWRITE  each candidate through `applyTabRewrites`, which re-derives the
//      occurrences precisely through the resolver (NAV10) and edits the buffer
//      undoably, then publish the per-tab notices.
//
// Query history is never touched — that half of NAV1 still stands.

import type { DatabaseEngineId } from '@/core/engines';

import { databaseEngineIdToSqlEngine } from '../../intellisense/versionGating/engineIdMapping';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { trackNavFeature } from '../analytics/trackNavFeature';
import { databaseComparisonKey } from '../connection-lifecycle/databaseIdentity';
import { getIdentifierIndexFor } from '../connection-lifecycle/navigationalLifecycle';
import { mssqlCatalogCaseSensitiveFor } from '../relationIdentity';
import { foldOeComponent } from '../oeIdentifier';
import type { QualifiedName, RenameBatch, RenameKind, TabCourtesyNotice } from '../types';

import { getMysqlLowerCaseTableNamesFor } from '../mysqlCaseProbe';

import { applyTabRewrites, type EngineCaseOptions, type RewritableTab } from './applyTabRewrites';
import { destinationOf } from './renameDestination';
import type { RenameBufferEditor } from './renameBufferEditors';
import { scanTabsForRenamedIdentifier, type ScanTabInput } from './renameTabScanner';
import { useRenameTabNoticeStore } from './renameTabNoticeStore';

/** The connection + database the rename actually ran against. */
export type RenameScope = {
  readonly connectionId: string;
  readonly database: string;
};

/** A collected tab plus the database it is bound to. An absent / empty
 *  database is a WILDCARD, as it always was: a tab that never picked one (or a
 *  rename driven without a database context) participates as an in-scope tab.
 *  Optional so an injected `collectOpenTabs` that predates cross-database
 *  scoping keeps working unchanged. */
export type PostApplyScanTab = ScanTabInput & {
  readonly database?: string;
};

/** Injectable seams for the pass. Every one defaults to the production store;
 *  unit tests override the ones they care about. */
export type PostApplyTabPassDeps = {
  /** Returns the open query tabs whose buffers should be scanned for the old
   *  identifier. Defaults to every `query`-typed tab on `scope.connectionId`,
   *  whatever database each is bound to — the scoping happens in the pass. */
  readonly collectOpenTabs?: (scope: RenameScope) => readonly PostApplyScanTab[];
  /** Receives the notices produced by the pass. Defaults to
   *  `useRenameTabNoticeStore.publishNotices`. */
  readonly publishNotices?: (notices: readonly TabCourtesyNotice[]) => void;
  /** Writes a rewritten buffer back to a tab that has no live Monaco editor.
   *  Defaults to `useWorkspaceStore.updateTabContent`. */
  readonly setTabSql?: (tabId: string, sql: string) => void;
  /** Live-editor lookup for the undoable in-place rewrite. Defaults to the
   *  `renameBufferEditors` registry. */
  readonly getEditor?: (tabId: string) => RenameBufferEditor | null;
  /** Per-engine identifier-case settings for the identity comparator.
   *  Defaults to {@link defaultEngineCaseOptions}. */
  readonly getEngineCaseOptions?: (
    scope: RenameScope,
    engineId: DatabaseEngineId,
  ) => EngineCaseOptions;
};

export function runPostApplyTabPass(
  batch: RenameBatch,
  scope: RenameScope,
  deps?: PostApplyTabPassDeps,
): void {
  const collect = deps?.collectOpenTabs ?? defaultCollectOpenTabs;
  const publish = deps?.publishNotices ?? defaultPublishNotices;
  const tabs = collect(scope);
  if (tabs.length === 0) return;

  // The destination this batch's own schema ALTER created — carried on the
  // batch, never re-derived here. `destinationOf` is the single reader; see
  // `RenameBatch.destination`.
  const destination = destinationOf(batch);

  const candidates = scanTabsForRenamedIdentifier(
    batch.target.fromIdentifier,
    destination.identifier,
    batch.target.kind,
    tabs,
  );
  if (candidates.length === 0) return;

  const byId = new Map(tabs.map((t) => [t.tabId, t]));
  const rewritable = candidates
    .map((n) => byId.get(n.tabId))
    .filter((t): t is PostApplyScanTab => t !== undefined);
  if (rewritable.length === 0) return;

  // Every candidate tab is bound to `scope.connectionId`, so they all share one
  // engine — read the case settings once off the first tab's engine id. They
  // describe the SCOPE's database, which is the right one for the
  // cross-database tabs too: the only reference they may rewrite is one that
  // spells that database.
  const engineId = rewritable[0].engineId;
  const caseOptions = (deps?.getEngineCaseOptions ?? defaultEngineCaseOptions)(scope, engineId);

  // Two groups, two targets — see {@link tabScopeOf} and
  // {@link crossDatabaseIdentifier}.
  const grouped = new Map<'own' | 'cross', RewritableTab[]>();
  for (const tab of rewritable) {
    const group = tabScopeOf(scope, engineId, batch.target.kind, tab);
    if (!group) continue;
    const bucket = grouped.get(group) ?? [];
    bucket.push({
      ...tab,
      crossDatabase: group === 'cross' && plannerGatesCrossScope(engineId),
    });
    grouped.set(group, bucket);
  }

  const notices: TabCourtesyNotice[] = [];
  let rewrittenTabs = 0;
  for (const [group, groupTabs] of grouped) {
    const fromIdentifier =
      group === 'cross'
        ? crossDatabaseIdentifier(batch.target.fromIdentifier, engineId, scope.database)
        : batch.target.fromIdentifier;
    const applied = applyTabRewrites(
      {
        connectionId: scope.connectionId,
        // The database the rename RAN in — the planner resolves every
        // reference against it, so a buffer in THIS tab that spells another
        // database (`B.dbo.users` typed in the `A` tab the rename ran in)
        // names a different object and is left alone. The cross-database TAB
        // split below is a different question and does not answer this one.
        database: scope.database,
        kind: batch.target.kind,
        fromIdentifier,
        destination,
        containingTable: batch.target.containingTable,
        ...caseOptions,
      },
      groupTabs,
      { setTabSql: deps?.setTabSql ?? defaultSetTabSql, getEditor: deps?.getEditor },
    );
    notices.push(...applied.notices);
    rewrittenTabs += applied.rewrittenTabs;
  }

  if (rewrittenTabs > 0) {
    trackNavFeature({
      name: 'feature:navfeatures_rename_tab_rewrite',
      props: { kind: batch.target.kind, tabs: rewrittenTabs },
    });
  }
  if (notices.length > 0) publish(notices);
}

/**
 * Which group a candidate tab belongs to — or `null` when it must not
 * participate at all.
 *
 * `'own'` is the tab bound to the database the rename ran in (an absent
 * database on either side is the wildcard it has always been). Sameness is the
 * ENGINE's answer, not `toLowerCase()`: PostgreSQL database names are
 * case-sensitive identifiers, so a rename in `Sales` must leave an open `sales`
 * tab alone, while a `_CI_` SQL Server instance really does have one `Sales`
 * however a tab spells it. `databaseComparisonKey` is that rule — the CONSUMER
 * variant, because this is a "same database?" question about the REQUEST and
 * not about which index the lifecycle files: the identity variant answers `''`
 * for every MySQL / Oracle / SQLite database (one index spans them all) and
 * would merge tabs on `DbA` and `DbB` into one scope.
 *
 * `'cross'` is a tab on a DIFFERENT database, kept only where a reference can
 * name another database at all:
 *   - `mssql` — `B.dbo.users`, the three-part name F2 renames through.
 *   - `mysql` — `db.table`: a MySQL schema IS a database, so its schema slot is
 *     the database component.
 *   - `oracle` — `HR.USERS`. An Oracle "database" in this area IS an owner, and
 *     ONE connection spans every owner the login can see with ONE index behind
 *     it (`connection-lifecycle/databaseIdentity.ts`), so a tab bound to
 *     `JAMSQL` reaches `HR.USERS` with a plain two-part reference — no dblink,
 *     no second connection. Dropping those tabs is what left the round-15 P2
 *     buffer with neither a rewrite nor a warning. Like MySQL's, the owner is
 *     carried by the SCHEMA component; unlike MySQL's, the planner's own gate
 *     cannot read it — see {@link plannerGatesCrossScope}.
 *   - `pgsql` — a plain reference cannot reach another database at all
 *     (PostgreSQL needs dblink / FDW), so such a tab can never denote the
 *     renamed object.
 *   - `sqlite` — one connection is one FILE; its tabs share that database.
 *   - `kusto` / `mock` — no rename surface of their own.
 *
 * RECORDED GAP (cross-engine parity): a `column` rename never collects
 * cross-database tabs on any engine. A cross-database column reference has to
 * be spelled four-part (`B.dbo.users.email`), and `QualifiedName` keeps only
 * three components — the database is the one the parser drops — so the pass
 * cannot prove such an occurrence names the renamed table's column rather than
 * the tab's own same-named one.
 *
 * The gap is narrower than it reads, and deliberately not closed here. A
 * four-part chain in a BUFFER does still carry its database: the chain's HEAD
 * holds it, and `bufferIdentifierScan.qualifierRelationOf` now hands it over,
 * which is what lets an OWN-database tab refuse `B.dbo.users.email` instead of
 * rewriting it. Admitting cross-database tabs for a column rename needs more
 * than that read — the column arm has no `crossDatabase` gate of its own, so it
 * would also have to REQUIRE the spelled database rather than merely check it —
 * and a five-part chain stays unreadable whatever we do. Left as one decision
 * for a slice that can test it end-to-end.
 *
 * ORACLE IS NOT THE EXCEPTION IT LOOKS LIKE. A cross-owner column reference
 * there is only THREE parts (`HR.USERS.EMAIL`), which `QualifiedName` does
 * hold — but the column arm still has no gate that REQUIRES the owner to be
 * spelled, and an Oracle cross-owner tab carries `crossDatabase: false` (see
 * {@link plannerGatesCrossScope}), so admitting it would let a bare
 * `USERS.EMAIL` in a `JAMSQL` tab be rewritten as the renamed table's column.
 * The exclusion therefore stays, for the same reason and with the same shape.
 */
function tabScopeOf(
  scope: RenameScope,
  engineId: DatabaseEngineId,
  kind: RenameKind,
  tab: PostApplyScanTab,
): 'own' | 'cross' | null {
  const tabDb = tab.database ?? '';
  if (!scope.database || !tabDb) return 'own';
  const key = (database: string): string =>
    databaseComparisonKey(scope.connectionId, engineId, database);
  if (key(tabDb) === key(scope.database)) return 'own';
  if (kind === 'column') return null;
  return engineId === 'mssql' || engineId === 'mysql' || engineId === 'oracle' ? 'cross' : null;
}

/**
 * Whether `tabRewritePlanner`'s own cross-scope gate can prove that a reference
 * in a tab bound elsewhere names the renamed object's database — i.e. whether
 * the `crossDatabase` flag means anything on this engine.
 *
 * The planner proves it by REQUIRING the reference to carry the component the
 * database lives in: `chain.qualified.foldedDatabase` everywhere, and
 * `foldedSchema` on MySQL, where a schema IS a database. An ORACLE owner also
 * lives in the schema component, but the planner reads `foldedDatabase` for it,
 * so raising the flag there drops EVERY occurrence — rewrite and warning alike
 * — which is exactly the shape the round-15 P2 reported. Oracle therefore
 * relies on the other half of the pair: {@link crossDatabaseIdentifier} makes
 * the renamed OWNER explicit on the target, and `matchRelationRef` then refuses
 * any reference that spells a different owner (`OTHER.USERS` → a different
 * schema component → no match).
 *
 * RECORDED GAP (cross-engine parity, deliberate): the two proofs part company
 * on a reference that spells NO owner at all. The planner's gate drops it
 * SILENTLY — in a `B` tab, a bare `users` is provably `B`'s own object. The
 * owner-qualified target instead grades it `ambiguous`, which the pass reports
 * as a courtesy notice. So renaming `HR.USERS` puts a "check this buffer"
 * banner on a `JAMSQL` tab holding a bare `USERS` that the SQL Server
 * equivalent would pass over in silence.
 *
 * That is the pass's UNIFORM rule for an unqualified reference against a
 * qualified target rather than a new special case — the tab bound to the
 * renamed owner ITSELF already gets the same notice for the same buffer — and
 * it errs the safe way: the bug being fixed reported nothing and rewrote
 * nothing. Oracle is also the engine where the notice is least often wrong, a
 * `PUBLIC SYNONYM` being the ordinary way a bare name in one owner's session
 * does resolve to another owner's table. Closing it is one condition in
 * `tabRewritePlanner.ts`'s `matchRelationOccurrence` (read `foldedSchema` for
 * `oracle` as well as `mysql`, in both the whole-chain gate and the three-part
 * arm); that file belongs to a concurrent change, so it is recorded here rather
 * than half-applied.
 */
function plannerGatesCrossScope(engineId: DatabaseEngineId): boolean {
  return engineId === 'mssql' || engineId === 'mysql';
}

/**
 * The renamed object's identity as a tab on ANOTHER database has to spell it:
 * with the database the rename ran in made EXPLICIT, so the identity
 * comparator can reject a reference that names a different one.
 *
 * Both the spelled and the folded component are set — the comparator reads the
 * folded one, the spelled one keeps the identity coherent for anything that
 * reports it — while `raw` is left alone so the notice still shows the name the
 * user renamed.
 *
 * `scope.database` is a CATALOG spelling (the tab's / connection's own
 * database, never source text), so it folds through `foldOeComponent` — the
 * same helper `relationIdentity.ts`'s `effectiveDatabaseFor` uses for the same
 * value, so the tab rewrites and the persisted-body rewrites draw one line.
 * Folding it as an UNQUOTED identifier instead destroys a name that can only
 * have been created quoted: an Oracle owner `"Sales"` became `SALES` here while
 * the buffer's `"Sales".USERS` stayed `Sales`, and the reference was dropped
 * silently. That was latent while only mssql / mysql tabs reached this — both
 * either fold case-insensitively or decide on the spelled half — and went live
 * with the Oracle owner group below.
 *
 * On MySQL and Oracle the component is the SCHEMA slot — a MySQL schema IS a
 * database, and an Oracle owner IS this area's database — so a reference in
 * another one's tab is proved by the very same schema comparison every in-scope
 * rewrite already runs. Filling it is load-bearing, not cosmetic: a rename
 * driven from a BARE cursor reference carries no schema on the target, and
 * without one a bare `USERS` in the other owner's tab would compare EQUAL and
 * be rewritten. On MySQL a reference that spells no database never reaches the
 * comparator at all (`tabRewritePlanner`'s `crossDatabase` gate drops it as the
 * tab's own object); on Oracle it reaches it and is reported — see
 * {@link plannerGatesCrossScope}.
 */
function crossDatabaseIdentifier(
  name: QualifiedName,
  engineId: DatabaseEngineId,
  database: string,
): QualifiedName {
  const engine = databaseEngineIdToSqlEngine(engineId);
  const folded = engine ? foldOeComponent(engine, database) : database;
  if (engineId === 'mysql' || engineId === 'oracle') {
    return name.foldedSchema ? name : { ...name, schema: database, foldedSchema: folded };
  }
  return name.foldedDatabase ? name : { ...name, database, foldedDatabase: folded };
}

function defaultCollectOpenTabs(scope: RenameScope): readonly PostApplyScanTab[] {
  const tabs = useWorkspaceStore.getState().tabs;
  const activeConnections = useConnectionStore.getState().activeConnections;
  const out: PostApplyScanTab[] = [];
  for (const tab of tabs) {
    if (tab.type !== 'query') continue;
    const sql = tab.content?.query;
    const connectionId = tab.connectionInfo?.connectionId;
    if (!sql || !connectionId) continue;
    if (connectionId !== scope.connectionId) continue;
    const conn = activeConnections.find((c) => c.id === connectionId);
    const engineId = conn?.connection?.engine as DatabaseEngineId | undefined;
    if (!engineId) continue;
    // The database is carried, never filtered on here: `tabScopeOf` decides
    // what a tab on another database may match.
    out.push({ tabId: tab.id, sql, engineId, database: tab.connectionInfo?.database ?? '' });
  }
  return out;
}

/** The same case seams the rename PLANNER reads, so the automatic tab rewrites
 *  and the persisted-body rewrites decide "is this the renamed object?" from
 *  one set of rules:
 *   - MySQL's `@@lower_case_table_names` comes off the connection's cached
 *     schema probe (`mysqlCaseProbe.ts`).
 *   - SQL Server's catalog collation comes off the INDEX HANDLE for the scope's
 *     (connection, database) — `mssqlCollationProbe.ts` reads each database's
 *     collation on the lifecycle's case-policy gate and the index freezes it as
 *     its object-key rule, so the handle reports the rule that was actually
 *     applied (see `mssqlCatalogCaseSensitiveFor`; the lifecycle resolves an
 *     empty `database` to the connection's configured one, which is why the
 *     handle — not a recomputation — is the source). Without it a rename of
 *     `dbo.V` in a `_CS_` database rewrote an open tab's `dbo.v`, a DIFFERENT
 *     object that the planner had correctly left alone.
 *  A scope with no index yet (nothing ever asked for that database) leaves the
 *  flag undefined — the documented case-insensitive default. */
function defaultEngineCaseOptions(
  scope: RenameScope,
  engineId: DatabaseEngineId,
): EngineCaseOptions {
  const index = getIdentifierIndexFor(scope.connectionId, scope.database);
  return {
    mysqlLowerCaseTableNames: getMysqlLowerCaseTableNamesFor(scope.connectionId, engineId),
    mssqlCatalogCaseSensitive: index
      ? mssqlCatalogCaseSensitiveFor(index, databaseEngineIdToSqlEngine(engineId))
      : undefined,
  };
}

function defaultSetTabSql(tabId: string, sql: string): void {
  useWorkspaceStore.getState().updateTabContent(tabId, { query: sql });
}

function defaultPublishNotices(notices: readonly TabCourtesyNotice[]): void {
  useRenameTabNoticeStore.getState().publishNotices(notices);
}
